import { AgGridReact } from 'ag-grid-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { getHHMMSSFromSeconds } from '../../../utils/TimeConversionFunctions';
import ColumnFilter from './ColumnFilter';

const NewPayrollReportTable = (props) => {
  const {
    reportData,
    pageNo,
    pageSize,
  } = props;

  const getColumnDefs = (data) => {
    let columnDefs = [
      { headerName: 'Sr No', field: 'srNo' },
      { headerName: 'Employee Id', field: 'employeeCode' },
      { headerName: 'Full Name', field: 'fullName' },
      { headerName: 'Office Location', field: 'officeLocation' },
      { headerName: 'Month', field: 'month' },
      {
        headerName: 'Deficit Hrs',
        field: 'deficitHrs',
        filter: ColumnFilter,
        filterParams: { field: 'deficitHrs' },
        sortable: true,
      },
      {
        headerName: 'Office Hrs',
        field: 'totalOfficeHours',
        filter: ColumnFilter,
        filterParams: { field: 'totalOfficeHours' },
        sortable: true,
      },
      {
        headerName: 'Expected Hrs',
        field: 'expectedHours',
        filter: ColumnFilter,
        filterParams: { field: 'expectedHours' },
        sortable: true,
      },
      {
        headerName: 'WFH Hrs',
        field: 'totalWfhHours',
        filter: ColumnFilter,
        filterParams: { field: 'totalWfhHours' },
        sortable: true,
      },
      {
        headerName: 'External Hours',
        field: 'totalExternalHours',
        filter: ColumnFilter,
        filterParams: { field: 'totalExternalHours' },
        sortable: true,
      },
      {
        headerName: 'Weekend Hrs',
        field: 'weekendHours',
        filter: ColumnFilter,
        filterParams: { field: 'weekendHours' },
        sortable: true,
      },
      {
        headerName: 'Weekend Working Hrs',
        field: 'totalWeekendWorkingHours',
        filter: ColumnFilter,
        filterParams: { field: 'totalWeekendWorkingHours' },
        sortable: true,
      },
      {
        headerName: 'WFH Days',
        field: 'workFromHomeDays',
        filter: ColumnFilter,
        filterParams: { field: 'workFromHomeDays' },
        sortable: true,
      },
      {
        headerName: 'Total Hrs',
        field: 'totalHours',
        filter: ColumnFilter,
        filterParams: { field: 'totalHours' },
        sortable: true,
      },
      {
        headerName: 'Auto Converted Leaves',
        field: 'autoConvertedLeaves',
        filter: ColumnFilter,
        filterParams: {
          field: 'autoConvertedLeaves',
        },
        sortable: true,
      },
      {
        headerName: 'Manual Casual Leave',
        field: 'manualCasualLeaves',
        filter: ColumnFilter,
        filterParams: {
          field: 'manualCasualLeaves',
        },
        sortable: true,
      },
      {
        headerName: 'Leaves',
        field: 'leaves',
        filter: ColumnFilter,
        filterParams: { field: 'leaves' },
        sortable: true,
      },
    ];

    // Dynamically add one column per leave category found in the data.
    // Key comes from leaveCategoryBreakdown map — no hardcoding needed.
    const breakdown = data?.[0]?.monthlyTimingsDTOList?.[0]?.leaveCategoryBreakdown;
    if (breakdown && typeof breakdown === 'object') {
      Object.keys(breakdown).forEach((categoryName) => {
        columnDefs.push({
          headerName: categoryName,
          field: 'leaveBreakdown_' + categoryName,
          sortable: true,
        });
      });
    }

    setColumnDefs(columnDefs);
  };
  const getRowData = (data) => {
    let rowData = [];

    let deficitTitle = 'deficitHrs';
    let officeHoursTitle = 'totalOfficeHours';
    let wfhHoursTitle = 'totalWfhHours';
    let externalHoursTitle = 'totalExternalHours';
    let weekendHoursTitle = 'weekendHours';
    let weekendWorkingHoursTitle = 'totalWeekendWorkingHours';
    let wfhDaysTitle = 'workFromHomeDays';
    let totalHoursTitle = 'totalHours';
    let expectedHoursTitle = 'expectedHours';
    let autoConvertedLeavesTitle = 'autoConvertedLeaves';
    let manualCasualLeavesTitle = 'manualCasualLeaves';
    let leavesTitle = 'leaves';
    for (let i = 0; i < data?.length; i++) {
      const personData = data[i];
      const numberOfMonths = personData?.monthlyTimingsDTOList?.length;
      for (let j = numberOfMonths - 1; j >= 0; j--) {
        const monthSummary =
          personData.monthlyTimingsDTOList[numberOfMonths - 1 - j];
        let currentRowData = {
          srNo:
            i * numberOfMonths +
            numberOfMonths -
            j +
            pageNo * pageSize * numberOfMonths,
          employeeCode: data[i].employeeCode,
          tenantId: data[i].tenantId,
          fullName: data[i].fullName,
          officeLocation: monthSummary.officeLocation || '',
        };
        const currentMonth = new Date(monthSummary.startDate);
        const monthName = currentMonth.toLocaleString('default', {
          month: 'long',
        });
        const year = currentMonth.toLocaleString('default', {
          year: 'numeric',
        });
        let monthTitle = monthName.concat(' ', year);
        currentRowData = { ...currentRowData, month: monthTitle };
        currentRowData = {
          ...currentRowData,
          [deficitTitle]: getHHMMSSFromSeconds(
            personData.monthlyTimingsDTOList[numberOfMonths - 1 - j]
              .deficitHrs / 1000,
          ),
          [officeHoursTitle]: getHHMMSSFromSeconds(
            personData.monthlyTimingsDTOList[numberOfMonths - 1 - j]
              .totalOfficeHours / 1000,
          ),
          [wfhHoursTitle]: getHHMMSSFromSeconds(
            personData.monthlyTimingsDTOList[numberOfMonths - 1 - j]
              .totalWfhHours / 1000,
          ),
          [externalHoursTitle]: getHHMMSSFromSeconds(
            personData.monthlyTimingsDTOList[numberOfMonths - 1 - j]
              .totalExternalHours / 1000,
          ),
          [weekendHoursTitle]: getHHMMSSFromSeconds(
            personData.monthlyTimingsDTOList[numberOfMonths - 1 - j]
              .weekendHours / 1000,
          ),
          [weekendWorkingHoursTitle]: getHHMMSSFromSeconds(
            (personData.monthlyTimingsDTOList[numberOfMonths - 1 - j]
              .totalWeekendWorkingHours || 0) / 1000,
          ),
          [wfhDaysTitle]:
            personData.monthlyTimingsDTOList[numberOfMonths - 1 - j]
              .workFromHomeDays,
          [totalHoursTitle]: getHHMMSSFromSeconds(
            personData.monthlyTimingsDTOList[numberOfMonths - 1 - j]
              .totalHours / 1000,
          ),
          [expectedHoursTitle]: getHHMMSSFromSeconds(
            personData.monthlyTimingsDTOList[numberOfMonths - 1 - j]
              .expectedHours / 1000,
          ),
          [autoConvertedLeavesTitle]:
            personData.monthlyTimingsDTOList[numberOfMonths - 1 - j]
              .autoConvertedLeaves,
          [manualCasualLeavesTitle]:
            personData.monthlyTimingsDTOList[numberOfMonths - 1 - j]
              .manualCasualLeaves,
          [leavesTitle]:
            personData.monthlyTimingsDTOList[numberOfMonths - 1 - j].leaves,
        };

        // Spread the per-category breakdown map into the row.
        // Each key becomes "leaveBreakdown_<LeaveName>" to avoid field name collisions.
        const breakdown = personData.monthlyTimingsDTOList[numberOfMonths - 1 - j]?.leaveCategoryBreakdown;
        if (breakdown && typeof breakdown === 'object') {
          Object.entries(breakdown).forEach(([categoryName, count]) => {
            currentRowData['leaveBreakdown_' + categoryName] = count ?? 0;
          });
        }

        rowData.push(currentRowData);
      }
    }
    return rowData;
  };

  const gridRef = useRef(); // Optional - for accessing Grid's API
  const [rowData, setRowData] = useState(); // Set rowData to Array of Objects, one Object per Row

  // DefaultColDef sets props common to all Columns
  const defaultColDef = useMemo(() => ({}));

  // Example load data from server
  useEffect(() => {
    const content = reportData?.data?.content;
    setRowData(getRowData(content));
    getColumnDefs(content);
  }, [reportData]);

  const [columnDefs, setColumnDefs] = useState();
  const [gridApi, setGridApi] = useState(null);

  const onGridReady = (params) => {
    setGridApi(params.api);
  };
  return (
    <>
      <div className="ag-theme-alpine" style={{ height: 540 }}>
        <AgGridReact
          ref={gridRef} // Ref for accessing Grid's API
          rowData={rowData} // Row Data for Rows
          columnDefs={columnDefs} // Column Defs for Columns
          defaultColDef={defaultColDef} // Default Column Properties
          animateRows={true} // Optional - set to 'true' to have rows animate when sorted
          rowSelection="multiple" // Options - allows click selection of rows
          suppressCellFocus={true}
          onGridReady={onGridReady}
        />
      </div>
    </>
  );
};
export default NewPayrollReportTable;
